/**
 * Older Think pages live in dock state, never inside useAgentChat `messages`.
 * Official `cf_agent_chat_messages` replaces that array in full
 * (`agents/chat/react.js` 773-783). Prepending via setMessages is wiped.
 */

export type ThinkOlderRow = { id?: string; role?: string; parts?: unknown };

export function hasMoreFromThinkHeader(header: string | null | undefined): boolean {
  return header === "1" || header === "true";
}

export function olderPagesCursor<T extends ThinkOlderRow>(
  olderPages: readonly (readonly T[])[],
  live: readonly T[],
): string | undefined {
  const firstOlder = olderPages[0]?.[0]?.id;
  if (typeof firstOlder === "string" && firstOlder.length > 0) return firstOlder;
  const firstLive = live[0]?.id;
  return typeof firstLive === "string" && firstLive.length > 0 ? firstLive : undefined;
}

export function flattenOlderPages<T extends ThinkOlderRow>(
  olderPages: readonly (readonly T[])[],
  live: readonly T[],
): T[] {
  const seen = new Set(
    live.map((row) => row.id).filter((id): id is string => typeof id === "string" && id.length > 0),
  );
  const out: T[] = [];
  for (const page of olderPages) {
    for (const row of page) {
      if (!row?.id || seen.has(row.id)) continue;
      seen.add(row.id);
      out.push(row);
    }
  }
  return out;
}

export function prependOlderPage<T extends ThinkOlderRow>(
  pages: readonly (readonly T[])[],
  incoming: readonly T[] | null | undefined,
  live: readonly T[],
): T[][] {
  const seen = new Set<string>();
  for (const row of live) {
    if (row?.id) seen.add(row.id);
  }
  for (const page of pages) {
    for (const row of page) {
      if (row?.id) seen.add(row.id);
    }
  }
  const older = (Array.isArray(incoming) ? incoming : []).filter(
    (row) => row?.id && !seen.has(row.id),
  );
  if (!older.length) return pages.map((page) => [...page]);
  return [older, ...pages.map((page) => [...page])];
}

/** Last id of the newest olderPages page (closest to the live window). */
export function newestOlderPageTailId<T extends ThinkOlderRow>(
  olderPages: readonly (readonly T[])[],
): string | undefined {
  const lastPage = olderPages[olderPages.length - 1];
  if (!lastPage?.length) return undefined;
  const last = lastPage[lastPage.length - 1];
  return typeof last?.id === "string" && last.id.length > 0 ? last.id : undefined;
}

export function appendGapPage<T extends ThinkOlderRow>(
  pages: readonly (readonly T[])[],
  incoming: readonly T[] | null | undefined,
  live: readonly T[],
): T[][] {
  const seen = new Set<string>();
  for (const row of live) {
    if (row?.id) seen.add(row.id);
  }
  for (const page of pages) {
    for (const row of page) {
      if (row?.id) seen.add(row.id);
    }
  }
  const gap = (Array.isArray(incoming) ? incoming : []).filter(
    (row) => row?.id && !seen.has(row.id),
  );
  if (!gap.length) return pages.map((page) => [...page]);
  return [...pages.map((page) => [...page]), gap];
}

export function gapPageConnectsToOlder<T extends ThinkOlderRow>(
  incoming: readonly T[],
  olderPages: readonly (readonly T[])[],
): boolean {
  const tail = newestOlderPageTailId(olderPages);
  if (!tail) return true;
  const incomingIds = new Set(
    incoming.map((row) => row.id).filter((id): id is string => typeof id === "string" && id.length > 0),
  );
  if (incomingIds.has(tail)) return true;
  for (const page of olderPages) {
    for (const row of page) {
      if (row?.id && incomingIds.has(row.id)) return true;
    }
  }
  return false;
}

export const THINK_OLDER_GAP_BACKFILL_MAX_PAGES = 32;

/**
 * After the live window slides, page official GET /get-messages
 * `before=live[0]` until the newest olderPages page is reached.
 * Writes only olderPages — never setMessages / frame rewrite.
 */
export async function backfillOlderPagesUntilConnected<T extends ThinkOlderRow>(opts: {
  olderPages: readonly (readonly T[])[];
  live: readonly T[];
  fetchPage: (before: string) => Promise<{
    messages: readonly T[];
    hasMoreHeader?: string | null;
    hasMore?: boolean;
  }>;
  maxPages?: number;
}): Promise<{ pages: T[][]; hasMoreHeader: string | null }> {
  const live0 = opts.live[0]?.id;
  if (typeof live0 !== "string" || !live0 || opts.olderPages.length === 0) {
    return { pages: opts.olderPages.map((page) => [...page]), hasMoreHeader: null };
  }
  const collected: T[][] = [];
  let before = live0;
  let hasMoreHeader: string | null = null;
  const maxPages = opts.maxPages ?? THINK_OLDER_GAP_BACKFILL_MAX_PAGES;
  for (let i = 0; i < maxPages; i += 1) {
    const page = await opts.fetchPage(before);
    hasMoreHeader = page.hasMoreHeader ?? null;
    const rows = Array.isArray(page.messages) ? [...page.messages] : [];
    collected.push(rows);
    const connected = gapPageConnectsToOlder(rows, opts.olderPages);
    const exhausted = rows.length === 0 || page.hasMore === false;
    if (connected || exhausted) break;
    const nextBefore = rows[0]?.id;
    if (typeof nextBefore !== "string" || !nextBefore || nextBefore === before) break;
    before = nextBefore;
  }
  let pages = opts.olderPages.map((page) => [...page]);
  for (const incoming of collected.slice().reverse()) {
    pages = appendGapPage(pages, incoming, opts.live);
  }
  return { pages, hasMoreHeader };
}
